/**
 * W3 — REAL EMPLOYEE ELIGIBILITY: the browser's view of the ONE canonical
 * predicate (field test 2026-10-16, Wave 3).
 *
 * The rule lives in SQL (migration 20270206050100) and is NEVER re-derived
 * here; this module only names its vocabulary and calls it:
 *
 *   technician_work_eligibility(technician_id)   → operational?, reason, families
 *   technician_is_schedulable(technician_id, f?) → gate-aware (observe = legacy)
 *   shop_schedulable_technician_ids(shop_id, f?) → gate-aware roster
 *   shop_work_eligibility_report / _summary      → diagnostics (managers)
 *   reconcile_technician_work_roles              → authorized grant (audited)
 *   acknowledge_technician_not_schedulable       → authorized exclusion (audited)
 *   set_shop_work_eligibility_mode               → the per-shop gate (audited)
 *
 * A roster row is OPERATIONAL only when it is Active, bound to a real active
 * account (technician_users), that account's profile is the row's profile, the
 * account holds an ACTIVE membership in the shop (accepted invitation, accepted
 * Connect offer or founding act), and the membership's STATED work roles give
 * it a work family (Technician → mechanical, Detail Specialist → detail).
 * Presence is availability, not eligibility.
 */
import { supabase } from '@/integrations/supabase/client';

export type WorkFamily = 'MECHANICAL' | 'DETAIL';
export type WorkEligibilityMode = 'observe' | 'enforce';

export const WORK_ELIGIBILITY_REASONS = [
  'OK',
  'NOT_FOUND',
  'NOT_VISIBLE',
  'NOT_EMPLOYED',
  'NO_ACCOUNT',
  'ACCOUNT_INACTIVE',
  'IDENTITY_CONFLICT',
  'NOT_JOINED',
  'WORK_ROLE_UNSTATED',
  'NO_WORK_ROLE',
] as const;
export type WorkEligibilityReason = typeof WORK_ELIGIBILITY_REASONS[number];

/** Plain-language reason for managers (Team Management, pickers). */
export const WORK_ELIGIBILITY_REASON_LABEL: Record<WorkEligibilityReason, string> = {
  OK: 'Schedulable',
  NOT_FOUND: 'Not on this team',
  NOT_VISIBLE: 'Not on this team',
  NOT_EMPLOYED: 'Not an active employee',
  NO_ACCOUNT: 'Has not joined OptiBay — invite them',
  ACCOUNT_INACTIVE: 'OptiBay account is not active',
  IDENTITY_CONFLICT: 'Roster record and account do not match',
  NOT_JOINED: 'Invitation not accepted, suspended or removed',
  WORK_ROLE_UNSTATED: 'Nobody has stated what work they do',
  NO_WORK_ROLE: 'Has no Technician or Detail Specialist work role',
};

/** Reasons a shop can resolve by a work-role decision (the person has joined). */
export function isResolvableByWorkRoleDecision(reason: WorkEligibilityReason): boolean {
  return reason === 'WORK_ROLE_UNSTATED' || reason === 'NO_WORK_ROLE';
}

export function isWorkEligibilityReason(value: unknown): value is WorkEligibilityReason {
  return typeof value === 'string' && (WORK_ELIGIBILITY_REASONS as readonly string[]).includes(value);
}

export interface TechnicianWorkEligibility {
  technicianId: string;
  shopId: string | null;
  operational: boolean;
  reason: WorkEligibilityReason;
  families: WorkFamily[];
  enforced: boolean;
}

export interface WorkEligibilityReportRow {
  technician_id: string;
  technician_name: string;
  employment_status: string | null;
  is_present: boolean | null;
  schedulable_legacy: boolean;
  operational: boolean;
  reason: WorkEligibilityReason;
  families: WorkFamily[];
  legacy_families: WorkFamily[];
  would_lose: boolean;
  has_account: boolean;
  membership_status: string | null;
  authority_role: string | null;
  legacy_role: string | null;
  work_roles: string[];
  work_roles_stated: boolean;
  proposed_work_roles: string[] | null;
  proposal_source: string | null;
  completed_timer_sessions: number;
  open_assigned_tickets: number;
  live_timers: number;
  duplicate_suspect: boolean;
  last_decision: string | null;
  last_decision_at: string | null;
}

export interface WorkEligibilitySummary {
  shop_id: string;
  mode: WorkEligibilityMode;
  roster_active: number;
  schedulable_legacy: number;
  operational: number;
  would_lose: number;
  would_lose_with_open_work: number;
  with_proposal: number;
  by_reason: Partial<Record<WorkEligibilityReason, number>>;
  would_lose_ids: string[];
  activation_blocked_empty_board: boolean;
}

/** PostgREST renders SETOF uuid as bare strings or single-key rows; accept both. */
function idsOf(data: unknown): string[] {
  return ((data ?? []) as unknown[]).map((r) =>
    typeof r === 'string' ? r : String(Object.values((r ?? {}) as Record<string, unknown>)[0] ?? ''),
  ).filter(Boolean);
}

export async function fetchTechnicianWorkEligibility(technicianId: string): Promise<TechnicianWorkEligibility | null> {
  if (!technicianId) return null;
  const { data, error } = await supabase.rpc('technician_work_eligibility' as never, { p_technician_id: technicianId } as never);
  if (error) {
    console.error('[workEligibility] technician_work_eligibility failed:', error);
    return null;
  }
  const raw = data as unknown;
  const row = (Array.isArray(raw) ? raw[0] : raw) as Record<string, unknown> | null | undefined;
  if (!row || !isWorkEligibilityReason(row.reason)) return null;
  return {
    technicianId,
    shopId: (row.shop_id as string | null) ?? null,
    operational: row.operational === true,
    reason: row.reason,
    families: ((row.families as WorkFamily[] | null) ?? []),
    enforced: row.enforced === true,
  };
}

/**
 * The shop's schedulable roster under its gate (observe = legacy, enforce =
 * operational only). `null` = unknown (the caller must not treat it as "all").
 */
export async function fetchSchedulableTechnicianIds(shopId: string, family?: WorkFamily): Promise<Set<string> | null> {
  if (!shopId) return null;
  const { data, error } = await supabase.rpc('shop_schedulable_technician_ids' as never, {
    p_shop_id: shopId,
    ...(family ? { p_family: family } : {}),
  } as never);
  if (error) {
    console.error('[workEligibility] shop_schedulable_technician_ids failed:', error);
    return null;
  }
  return new Set(idsOf(data));
}

export async function fetchWorkEligibilityReport(shopId: string): Promise<WorkEligibilityReportRow[]> {
  const { data, error } = await supabase.rpc('shop_work_eligibility_report' as never, { p_shop_id: shopId } as never);
  if (error) throw error;
  return (data ?? []) as unknown as WorkEligibilityReportRow[];
}

export async function fetchWorkEligibilitySummary(shopId: string): Promise<WorkEligibilitySummary> {
  const { data, error } = await supabase.rpc('shop_work_eligibility_summary' as never, { p_shop_id: shopId } as never);
  if (error) throw error;
  return data as unknown as WorkEligibilitySummary;
}

/** Grant Technician and/or Detail Specialist to a JOINED person (audited decision). */
export async function reconcileTechnicianWorkRoles(
  technicianId: string,
  grant: ReadonlyArray<'TECHNICIAN' | 'DETAIL_SPECIALIST'>,
  reason: string,
): Promise<{ decision_id: string; operational: boolean; reason: WorkEligibilityReason }> {
  const { data, error } = await supabase.rpc('reconcile_technician_work_roles' as never, {
    p_technician_id: technicianId,
    p_grant: [...grant],
    p_reason: reason,
  } as never);
  if (error) throw error;
  return data as unknown as { decision_id: string; operational: boolean; reason: WorkEligibilityReason };
}

export async function acknowledgeTechnicianNotSchedulable(technicianId: string, reason: string): Promise<string> {
  const { data, error } = await supabase.rpc('acknowledge_technician_not_schedulable' as never, {
    p_technician_id: technicianId,
    p_reason: reason,
  } as never);
  if (error) throw error;
  return data as unknown as string;
}

/**
 * Change the shop's gate. ENFORCE requires `acknowledgedTechnicianIds` to be
 * EXACTLY the current would-lose population (the server re-checks and refuses
 * a stale list) and is refused while it would empty the board.
 */
export async function setShopWorkEligibilityMode(
  shopId: string,
  mode: WorkEligibilityMode,
  reason: string,
  acknowledgedTechnicianIds: readonly string[] = [],
): Promise<{ decision_id: string; mode: WorkEligibilityMode }> {
  const { data, error } = await supabase.rpc('set_shop_work_eligibility_mode' as never, {
    p_shop_id: shopId,
    p_mode: mode,
    p_reason: reason,
    p_acknowledged_technician_ids: [...acknowledgedTechnicianIds],
  } as never);
  if (error) throw error;
  return data as unknown as { decision_id: string; mode: WorkEligibilityMode };
}
